// Component styling constants
export const COMPONENT_STYLES = {
  // Layout and container classes with responsive max-width progression
  LAYOUT: {
    // Main content wrapper with progressive max-width matching CSS variables
    CONTENT_WRAPPER:
      'mx-auto w-full ' +
      'px-4 sm:px-6 md:px-8 ' +
      'min-[1024px]:max-w-[1280px] min-[1024px]:px-16 ' +
      'min-[1440px]:max-w-[1440px] min-[1440px]:pl-[136px] min-[1440px]:pr-[97px]',

    // Tight variant with reduced padding
    CONTENT_WRAPPER_TIGHT:
      'mx-auto ' +
      'max-w-[420px] ' +
      'min-[640px]:max-w-[640px] ' +
      'min-[768px]:max-w-[768px] ' +
      'min-[1024px]:max-w-[1040px] ' +
      'min-[1280px]:max-w-[1280px] ' +
      'min-[1536px]:max-w-[1360px] ' +
      'px-2 sm:px-3 lg:px-5',

    // Wide variant with larger max-widths at each breakpoint
    CONTENT_WRAPPER_WIDE:
      'mx-auto ' +
      'max-w-[520px] ' + // Start wider
      'min-[640px]:max-w-[800px] ' +
      'min-[768px]:max-w-[980px] ' +
      'min-[1024px]:max-w-[1240px] ' +
      'min-[1280px]:max-w-[1440px] ' +
      'min-[1536px]:max-w-[1520px] ' +
      'px-3 sm:px-4 lg:px-8',

    // Section wrapper using same progression but capping at xl (1280px)
    SECTION_WRAPPER:
      'mx-auto ' +
      'max-w-[420px] ' +
      'min-[640px]:max-w-[640px] ' +
      'min-[768px]:max-w-[768px] ' +
      'min-[1024px]:max-w-[1040px] ' +
      'min-[1280px]:max-w-[1280px] ' + // Cap at desktop
      'min-[1536px]:max-w-[1280px] ' + // Stay capped
      'px-3 sm:px-4 lg:px-8',
  },

  CARD: {
    GLASS: 'bg-theme-glass-white backdrop-blur-md border border-white/30',
    FAINT_GLASS:
      'bg-surface-glass-bright backdrop-blur-md border border-white/30',
  },

  // FAQ Section constants
  FAQ: {
    SECTION:
      'py-8 md:py-12 bg-surface-success-subtle mt-8 md:mt-12 px-4 md:px-8 lg:px-12',
    HEADING:
      'text-left font-extrabold text-[24px] leading-[30px] lg:text-[36px] lg:leading-[44px] text-theme-primary mb-10',
    ACCORDION_CONTAINER: 'w-full flex flex-col gap-4 md:gap-6 bg-transparent',
    ITEM_CONTAINER:
      'w-full p-4 bg-theme-glass-white backdrop-blur-md border border-white/30',
    BUTTON: 'flex justify-between w-full gap-4 text-left',
    QUESTION:
      'text-[20px] leading-[24px] lg:text-[26px] lg:leading-[32px] tracking-[-0.52px] text-theme-primary text-left font-bold',
    ICON_CONTAINER: 'h-8 aspect-square rounded-full bg-surface-icon-bg flex',
    ICON_OPEN: 'w-3 h-[2px] bg-[rgba(13,27,74,1)] m-auto',
    ICON_CLOSED: 'text-[22px] text-[rgba(13,27,74,1)] m-auto',
    ANSWER_CONTAINER: (isOpen: boolean) =>
      `w-full grid transition-[grid-template-rows] overflow-hidden duration-300 ease-out ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`,
    ANSWER_CONTENT: (isOpen: boolean) =>
      `transition-[padding] duration-500 ease-out ${isOpen && 'pt-2'} min-h-0`,
  },

  // RichText variants with list support
  RICH_TEXT_VARIANTS: {
    cardDescription: `
      flex flex-col gap-2 text-text-muted-light text-lg h-full font-Regular [&>p>b]:text-white
      [&>p>b]:font-ExtraBold text-pretty whitespace-pre-line
    `,
    promotionalBanner: `
      flex flex-col gap-2 text-text-muted-light text-lg h-full font-Regular [&>p>b]:text-white
      [&>p>b]:font-ExtraBold text-pretty whitespace-pre-line
      flex-1 [&>ul>li>p>a]:text-theme-information [&>ul>li]:pt-1 even:pl-2 odd:pr-2 md:text-1.25
    `,
    appointmentPayment: `
      font-Regular text-text-warm-inverse   
              [&>ul>li]:list-disc [&>ul>li]:ml-4 [&>ul>li]:text-text-muted-light    [&>p:first-child]:text-text-muted-light  
              [&>p:nth-child(2)]:pt-[24px]
    `,
    faqNestedList: `
      text-[18px] leading-[22px] lg:leading-[26px] tracking-[-0.48px] text-text-main font-normal
      [&>p>b]:text-theme-primary [&>p>b]:font-semibold
      [&>ul>li>div>p]:max-w-full [&>ul>li>div>p]:inline
      [&>ul>li>div>b]:text-theme-primary [&>ul>li>div>b]:font-semibold
      [&>ul>li>div>ul]:w-full
      [&>ul>li>div>ul>li]:flex-nowrap [&>ul>li>div>ul>li]:items-start
      [&>ul>li>div>ul>li>div>p]:transform [&>ul>li>div>ul>li>div>p]:-translate-y-1 md:[&>ul>li>div>ul>li>div>p]:translate-y-0
      [&>ul>li>div>h3]:text-2xl [&>ul>li>div>h3]:leading-8 [&>ul>li>div>h3]:text-theme-primary [&>ul>li>div>h3]:font-extrabold
      [&>ul>li>div>h3>b]:text-2xl [&>ul>li>div>h3>b]:leading-8 [&>ul>li>div>h3>b]:text-theme-primary [&>ul>li>div>h3>b]:font-extrabold
      [&>p>a]:inline [&>p>a]:font-normal [&>p>a]:underline [&>p>a]:block [&>p>a]:pt-4 [&>p>a:hover]:text-theme-primary
      lg:[&>ul>li>div>h3]:text-2xl lg:[&>ul>li>div>h3]:leading-8 lg:[&>ul>li>div>h3>b]:text-2xl lg:[&>ul>li>div>h3>b]:leading-8
    `,
    heading: 'text-4xl md:text-6xl font-bold mb-6',
    body: 'text-base leading-relaxed',
    caption: 'text-sm text-gray-600',
    unstyled: '',
  },

  TEXT: {
    HEADING_PRIMARY: 'text-heading-dark',
    HEADING_BOLD_ACCENT: 'text-primary font-bold text-xl',
    HEADER_NAV:
      'font-mulish font-bold text-[16px] leading-p-lg tracking-normal text-text-default',
    HEADING_SIZES: {
      SM: 'text-2xl md:text-3xl',
      MD: 'text-3xl md:text-4xl',
      LG: 'text-4xl md:text-5xl',
      XL: 'text-5xl md:text-6xl',
      HERO: 'text-2xl md:text-[32px]', // 32px standard for hero headings
    },
    CONTENT_SIZES: {
      DESCRIPTION: 'text-sm md:text-[14px]', // 14px standard for content descriptions
    },
    HERO_DESCRIPTION:
      'text-[16px] font-mulish font-normal leading-[30px] tracking-[0.2px] text-text-default', // Figma specs for hero description
  },
  RICH_TEXT: {
    BASE: 'prose prose-lg max-w-none text-heading-dark',
    BOLD_ACCENT:
      'prose-strong:text-primary prose-strong:font-bold prose-strong:text-xl',
    COMBINED:
      'prose prose-lg max-w-none text-heading-dark prose-strong:text-primary prose-strong:font-bold prose-strong:text-xl',
  },
} as const;

export type RichTextVariant = keyof typeof COMPONENT_STYLES.RICH_TEXT_VARIANTS;
